import Add from "@mui/icons-material/Add";
import EditOutlined from "@mui/icons-material/EditOutlined";
import {
  Box,
  Button,
  CardActions,
  Checkbox,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { useGetAllReasonsQuery } from "app/api/order-incomplete-reasons";
import ErrorDisplay from "components/ErrorDisplay";
import LoadingDisplay from "components/LoadingDisplay";
import { OrderIncompleteReason } from "models/OrderIncompleteReason";
import { useState } from "react";
import UpsertReasonDialog from "../dialogs/UpsertReasonDialog";

const OrderIncompleteReasonsSection: React.FC = () => {
  const [includeArchived, setIncludeArchived] = useState(false);
  const { data: reasons, isLoading, error } = useGetAllReasonsQuery({ archived: includeArchived });

  const [isUpsertDialogOpen, setIsUpsertDialogOpen] = useState(false);
  const [reasonToEdit, setReasonToEdit] = useState<OrderIncompleteReason | null>(null);

  if (isLoading) return <LoadingDisplay message="Laddar anledningar..." />;
  if (error) return <ErrorDisplay message="Fel vid hämtning av anledningar" />;

  return (
    <>
      <Stack direction="row" justifyContent="space-between" px={2} py={1}>
        <Box>
          <Typography fontSize="larger">Anledningar</Typography>
          <Typography variant="subtitle2">
            Valbara alternativ för varför en order inte kan slutföras.
          </Typography>
        </Box>

        <FormControlLabel
          label="Visa arkiverade"
          labelPlacement="start"
          control={
            <Checkbox
              checked={includeArchived}
              onChange={() => {
                setIncludeArchived(!includeArchived);
              }}
            />
          }
        />
      </Stack>

      {reasons?.length && (
        <List disablePadding>
          {reasons?.map((reason) => (
            <ListItem
              key={reason.id}
              divider
              secondaryAction={
                <IconButton
                  onClick={() => {
                    setReasonToEdit(reason);
                    setIsUpsertDialogOpen(true);
                  }}
                >
                  <EditOutlined />
                </IconButton>
              }
            >
              <ListItemText
                primary={reason.description}
                secondary={reason.id}
                color={reason.archived ? "text.secondary" : "text.primary"}
                slotProps={{
                  primary: { sx: { textDecoration: reason.archived ? "line-through" : "none" } },
                  secondary: { sx: { fontFamily: "monospace" } },
                }}
              />
            </ListItem>
          ))}
        </List>
      )}

      <CardActions sx={{ justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          onClick={() => {
            setReasonToEdit(null);
            setIsUpsertDialogOpen(true);
          }}
        >
          Ny anledning
        </Button>
      </CardActions>

      {isUpsertDialogOpen && (
        <UpsertReasonDialog
          reason={reasonToEdit}
          onClose={() => {
            setIsUpsertDialogOpen(false);
            setReasonToEdit(null);
          }}
        />
      )}
    </>
  );
};

export default OrderIncompleteReasonsSection;
